import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import {
  AggregateResultRequest,
  AggregateResultResponse,
  GetAggregateResultQuery,
  GetAggregateResultQueryVariables,
  GetSyntheticResultQuery,
  GetSyntheticResultQueryVariables,
  QueryRecord,
  ResultAction,
} from '../../../../API';
import { ApiService } from '../../../shared/services/api.service';
import { GetAggregateResult, GetSyntheticResult } from '../../../../graphql/queries';
import { NzMessageService } from 'ng-zorro-antd/message';
import { ChartDataService } from '../../../bi/services/chart-data.service';

@Component({
  selector: 'app-aggregate-query-result',
  templateUrl: './aggregate-query-result.component.html',
  styleUrl: './aggregate-query-result.component.scss',
})
export class AggregateQueryResultComponent implements OnInit, OnDestroy, OnChanges {
  totalCount?: number;
  isLoading?: boolean;
  searchValue!: string;
  @Input() queryRecord!: QueryRecord;
  @Input() hideMetadata: boolean = false;
  @Input() isSynthetic: boolean = false;
  @Input() parent?: 'chart' | 'query';

  rawData: any[] = [];
  headers: any[] = [];
  rows: any[] = [];

  constructor(
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    private chartDataService: ChartDataService,
  ) {}

  ngOnInit(): void {
    if (this.isSynthetic) {
      this.getSyntheticAggregateResult();
    } else {
      this.getAggregateResults();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['queryRecord'] && !this.isSynthetic) {
      this.getAggregateResults();
    } else if (changes['queryRecord'] && this.isSynthetic) {
      this.getSyntheticAggregateResult();
    }
  }

  ngOnDestroy(): void {
    this.totalCount = undefined;
    this.isLoading = undefined;
    this.searchValue = '';
    this.rawData = [];
    this.headers = [];
    this.rows = [];
  }
  reset(): void {
    this.searchValue = '';
    this.search();
  }

  search(): void {
    this.rows = this.rawData?.filter((item: any) =>
      Object.values(item).some((value: any) => `${value}`.toLowerCase().includes(this.searchValue.toLowerCase())),
    );
  }

  setHeaders() {
    this.headers = this.headers.map((key) => {
      return {
        key: key,
        label: key,
        sortOrder: null,
        sortFn: (a: any, b: any) => this.queryHeaderSortFn(a, b, key),
        sortDirections: ['ascend', 'descend', null],
      };
    });
  }

  queryHeaderSortFn(a: any, b: any, key: string): number {
    const valueA = a[key];
    const valueB = b[key];

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return valueA - valueB;
    } else if (valueA instanceof Date && valueB instanceof Date) {
      return valueA.getTime() - valueB.getTime();
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      return valueA.localeCompare(valueB, undefined, { sensitivity: 'base' });
    } else {
      return 0;
    }
  }

  downloadCSV(): void {
    const headers = this.headers.map((header: any) => header.label);
    const rows = this.rawData.map((item: any) => this.headers.map((header: any) => item[header.key]));

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `result-${this.queryRecord?.queryId}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  getAggregateResults(): void {
    this.isLoading = true;
    const { queryId, engagement, athenaExecutionId } = this.queryRecord;

    const aggregateQueryRequest: AggregateResultRequest = {
      queryId,
      engagement,
      athenaExecutionId: athenaExecutionId!,
      action: ResultAction.GET_AGGREGATE_RESULT,
    };
    this.apiService
      .query<GetAggregateResultQuery, GetAggregateResultQueryVariables>(GetAggregateResult, {
        resultRequest: aggregateQueryRequest,
      })
      .then((response) => {
        const results = response.data?.GetAggregateResult;
        this.handleResult(results as AggregateResultResponse);
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to fetch results due to error ${error}`);
      })
      .finally(() => (this.isLoading = false));
  }

  getSyntheticAggregateResult(): void {
    this.isLoading = true;
    const { queryId, engagement, athenaExecutionId } = this.queryRecord;

    const aggregateQueryRequest: AggregateResultRequest = {
      queryId,
      engagement,
      athenaExecutionId: athenaExecutionId!,
      action: ResultAction.GET_SYNTHETIC_RESULT,
    };
    this.apiService
      .query<GetSyntheticResultQuery, GetSyntheticResultQueryVariables>(GetSyntheticResult, {
        resultRequest: aggregateQueryRequest,
      })
      .then((response) => {
        const results = response.data?.GetSyntheticResult;
        this.handleResult(results as AggregateResultResponse);
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to fetch results due to error ${error}`);
      })
      .finally(() => (this.isLoading = false));
  }

  handleResult(results: AggregateResultResponse): void {
    this.headers = results!.headers!;
    this.rawData = this.rows = JSON.parse(results!.rows!);
    if (this.parent === 'chart') {
      this.chartDataService.setData(this.rawData);
    }
    this.setHeaders();
    this.totalCount = this.rows.length;
  }
}
